<script setup lang="ts">
import { useFetchProductCarousels } from '~/data/product.ts';
import { PhCaretLeft, PhCaretRight } from '@phosphor-icons/vue';
import { useLoganStore } from '~/stores/logan';
const { $cathodeClient } = useNuxtApp();
import { storeToRefs } from 'pinia';
import ProductCard from '../ProductCard.vue';
// import { Button } from '@laam/ui/base';
interface Props {
	meta: { title: string; key: string };
	productId: number;
	dropHandle: string;
	lastIndex: boolean;
}

const carousel = ref<HTMLDivElement | null>(null);
const disableLeft = ref(true);
const disableRight = ref(false);

const props = defineProps<Props>();

const { data: carouselData } = useFetchProductCarousels(
	props.meta.key,
	props.productId,
	props.dropHandle,
);

const dataSource = ref<{
	dataSource: string;
	clickedFrom: string[];
	version: string | undefined;
}>({
	dataSource: '',
	clickedFrom: [props.meta.key],
	version: '',
});

const updateButtonStates = () => {
	if (carousel.value) {
		const scrollLeft = carousel.value.scrollLeft;
		const scrollWidth = carousel.value.scrollWidth;
		const clientWidth = carousel.value.clientWidth;

		disableLeft.value = scrollLeft <= 0;
		disableRight.value = scrollLeft + clientWidth >= scrollWidth;
	}
};

const scrollLeft = () => {
	if (carousel.value) {
		const cardWidth = (
			carousel.value.firstElementChild as HTMLDivElement | null
		)?.offsetWidth;
		cardWidth &&
			carousel.value.scrollBy({ left: -cardWidth, behavior: 'smooth' });
		setTimeout(updateButtonStates, 300);
	}
};

const scrollRight = () => {
	if (carousel.value) {
		const cardWidth = (
			carousel.value.firstElementChild as HTMLDivElement | null
		)?.offsetWidth;
		carousel.value.scrollBy({ left: cardWidth, behavior: 'smooth' });
		setTimeout(updateButtonStates, 300);
	}
};

onMounted(() => {
	updateButtonStates();
});

const logan = useLoganStore();
const { isHvc: isLoganHvc, isSignedIn } = storeToRefs(logan);
const isHvc = computed(() => isLoganHvc.value && isSignedIn.value);
provide('carouselTitle', props.meta.title);

const data = computed(() => {
	if (!carouselData.value) {
		return [];
	}
	if ('products' in carouselData.value) {
		return carouselData.value.products;
	}
	return carouselData.value;
});
</script>

<template>
	<div
		v-if="data && data.length"
		class="px-4xl lg:px-0"
		:class="isMobileView() && props.lastIndex && 'mb-[20vh]'"
	>
		<h2 class="text-xl font-semibold">{{ props.meta.title }}</h2>
		<div class="relative">
			<div
				v-if="!isMobileView()"
				:disabled="disableLeft"
				class="absolute left-[2%] top-1/3 transform -translate-y-1/3 flex items-center justify-center w-10xl h-10xl z-10 bg-white rounded-full shadow-md"
				@click="scrollLeft"
			>
				<PhCaretLeft :class="{ 'text-gray-300': disableLeft }" size="20" />
			</div>
			<div ref="carousel" class="flex w-full overflow-auto no-scrollbar my-4xl">
				<div
					v-for="(product, index) in data"
					:key="index"
					class="carousel-item"
				>
					<ProductCard
						:data="product"
						:horizontal-index="index"
						:data-source="dataSource"
						page="PLP"
						:is-hvc="isHvc"
						:cathode-client="$cathodeClient"
						:impression-debounce-time="
							useRuntimeConfig().public.impressionDebounceTime
						"
					>
						<template #image>
							<LazyImage
								:src="product.image.src"
								:alt="product.image.alt"
								class="w-full"
								width="50"
							/>
						</template>
						<template #top-right>
							<WishlistButton
								:product-id="product.shopify_id"
								:image-url="product.image.src"
								:image-alt="product.image.alt"
							/>
						</template>
					</ProductCard>
				</div>
			</div>
			<div
				v-if="!isMobileView()"
				:disabled="disableRight"
				class="absolute right-[2%] top-1/3 transform -translate-y-1/3 flex items-center justify-center w-10xl h-10xl z-10 bg-white rounded-full shadow-md"
				@click="scrollRight"
			>
				<PhCaretRight :class="{ 'text-gray-300': disableRight }" size="20" />
			</div>
		</div>
	</div>
</template>

<style scoped>
.carousel-item {
	@apply relative flex-none rounded-medium border-neutral-200 lg:pr-7xl pr-2xl;
	width: 50%;
	min-width: 41%;
	flex: 0 0 auto;
	border-radius: 10px;
	transition: box-shadow 0.2s ease;
	overflow: hidden;
}

/* medium screen */
@media (min-width: 768px) {
	.carousel-item {
		width: 25%;
		min-width: 23%;
	}
}
</style>
